// Ubicación en el repo: src/lib/catalogImages.js
// Imagen de producto del catálogo — tabla `catalog.image_path` + bucket
// privado `catalog-images` de Supabase Storage (migración 0051,
// 26-ago-2026, parte de la cotización de audífonos imprimible). Mismo
// patrón que branchLogos.js — ver ese archivo para el razonamiento
// completo; acá solo cambia el bucket y que la ruta usa el id del
// PRODUCTO en vez del de la sucursal.
import { supabase } from './supabaseClient';
import { compressImageIfNeeded } from './imageCompression';

const BUCKET = 'catalog-images';
// Mismo límite fijado en el bucket (migración 0051) — se valida acá antes
// de subir para dar un mensaje claro en vez del error genérico de Storage.
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

function imageCompressOpts() {
  return { maxDimension: 800, jpegQuality: 0.85, skipUnderBytes: 200 * 1024, preserveType: true };
}

// Sube (o reemplaza) la foto de un producto y actualiza `image_path`.
// `catalogId` debe existir ya en la tabla `catalog`.
export async function uploadCatalogImage(catalogId, file, previousImagePath) {
  const toUpload = await compressImageIfNeeded(file, imageCompressOpts());

  if (toUpload.size > MAX_FILE_SIZE_BYTES) {
    const mb = (toUpload.size / (1024 * 1024)).toFixed(1);
    throw new Error(`La imagen pesa ${mb} MB — el máximo permitido es 2 MB.`);
  }
  const safeName = toUpload.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const path = `${catalogId}/${crypto.randomUUID()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, toUpload, { contentType: toUpload.type || undefined });
  if (uploadError) throw uploadError;

  const { error: updateError } = await supabase.from('catalog').update({ image_path: path }).eq('id', catalogId);
  if (updateError) {
    // No dejar el archivo huérfano en Storage si no se pudo guardar la referencia.
    await supabase.storage.from(BUCKET).remove([path]);
    throw updateError;
  }

  if (previousImagePath) {
    await supabase.storage.from(BUCKET).remove([previousImagePath]);
  }
  return path;
}

export async function removeCatalogImage(catalogId, imagePath) {
  const { error } = await supabase.from('catalog').update({ image_path: null }).eq('id', catalogId);
  if (error) throw error;
  if (imagePath) {
    await supabase.storage.from(BUCKET).remove([imagePath]);
  }
}

// URL firmada de corta duración (el bucket es privado) — usada tanto para
// la vista previa en el formulario de Catálogo como en la cotización
// imprimible.
export async function getSignedCatalogImageUrl(imagePath) {
  if (!imagePath) return null;
  try {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(imagePath, 60);
    if (error) throw error;
    return data.signedUrl;
  } catch {
    // La foto es un detalle visual, no debe bloquear el resto de la
    // pantalla ni una impresión si algo falla (permiso, archivo borrado).
    return null;
  }
}
